import { useCallback, useEffect, useState } from "react";
import cancelIcon from "../../assets/cancelIcon.svg";
import { format, millisecondsToMinutes, millisecondsToSeconds } from "date-fns";
import LoadingComponent from "../../components/Loading";
import debounce from "../../utils/Debounce";
import axiosCMS from "../../libs/axios/axiosCMS";
import { convertFormatedDate } from "../../utils/FunctionHelpers";

const RetentionRateModal = ({ isOpen, onClose, selectedFilters }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const rowsPerPage = 5;

  const indexOfLastData = currentPage * rowsPerPage;
  const indexOfFirstData = indexOfLastData - rowsPerPage;

  /**
   * Fetches data from the API based on the selected filters.
   * Uses debounce to limit the number of API calls.
   * @param {Object} filters - The filters to apply when fetching data.
   */
  const getData = useCallback(debounce(async (filters) => {
    setIsLoading(true);
    try {
      const URL = 'user-engagement/retention_user_detail';
      const response = await axiosCMS.post(URL, filters);
      const result = response.data;
      setData(result.data);
    } catch (error) {
      console.error('Error fetching data: ', error);
    }
    setIsLoading(false);
  }, 1500), []);

  useEffect(() => {
    if (isOpen) {
      getData(selectedFilters);
      setCurrentPage(1);
    }
  }, [isOpen]);

  const dataPaginator = () => {
    const paginated = data.slice(indexOfFirstData, indexOfLastData);
    setPaginatedData(paginated);
  }

  useEffect(() => {
    dataPaginator();
  }, [currentPage, data]);

  const paginate = (direction) => {
    if (direction === 'next' && currentPage < Math.ceil(data.length / rowsPerPage)) {
      setCurrentPage(currentPage + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }
  if (!isOpen) return null;

  return (
    <div className={`ue-retention-rate ${isOpen ? "open" : ""}`}>
      <div className="ue-retention-rate__content">
        <div className="ue-retention-rate__header">
          <h1 className="ue-retention-rate__title">Retained Users</h1>
          <button className="ue-retention-rate__btn-close" onClick={onClose}>
            <img src={cancelIcon} alt="cancel icon" />
          </button>
        </div>
        <div className="ue-retention-rate__separator" />
        <div className="ue-retention-rate__section">
          <div className="ue-retention-rate__box">
            <table className="ue-retention-rate__table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Last Activity Date</th>
                  <th>Avg. Duration/Session</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? <tr>
                    <td colSpan={3}><LoadingComponent message={'Loading data...'} /></td>
                  </tr>
                  : paginatedData.length !== 0
                    ? paginatedData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.email}</td>
                        <td>
                          {item.last_chat ? convertFormatedDate(item.last_chat) : "-"}
                        </td>
                        <td>{item.avg_duration_session >= 60000 ? millisecondsToMinutes(item.avg_duration_session) : millisecondsToSeconds(item.avg_duration_session)}<span> {item.avg_duration_session > 60000 ? 'minutes' : 'seconds'}</span></td>
                        <td>{item.role?.role_name}</td>
                      </tr>
                    ))
                    : <tr>
                      <td colSpan={4} className="text-center">No data available</td>
                    </tr>
                }
              </tbody>
            </table>
            <div className="ue-retention-rate__pagination">
              <button onClick={() => paginate('prev')} disabled={currentPage === 1}>Previous</button>
              <button onClick={() => paginate('next')} disabled={currentPage === (Math.ceil(data.length / rowsPerPage) >= 1 ? Math.ceil(data.length / rowsPerPage) : 1)}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RetentionRateModal;